/* eslint-disable complexity */
import { useContext, useEffect, useMemo, useState } from 'react'
import {
  Grid,
  ListItemText,
  Hidden,
  ClickAwayListener,
  Box,
  SearchBarSearchIcon,
  useTheme
} from '@hermes/web-components'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { HomePageContext } from '../../../../providers/HomePage'

import { HomePageViewProps } from '../../../SearchPage/common/types'

import CustomAutocomplete from '../CustomAutocomplete'
import useHomePageSearchBarConfig from '../../../search/hooks/useHomePageSearchBarConfig'
import ConditionalRenderBox from '../../../SearchPage/Alternative/SearchBar/ConditionalRenderBox'
import { newSearchTypesPractices } from '../../../../constants/global'
import {
  StyledIconButton,
  CustomMenuItem,
  StyledMenuList,
  SearchBarContainer,
  whiteColor,
  SearchButtonContainer,
  SearchIconButton,
  AutocompleteSearchTextField
} from '../styles'
import LoadingWheel from '../../../Icons/LoadingWheel'
import { InputGradientText, InputText, StyledChevronRightIcon } from './styles'
import TypeMenuItemBlock from '../MobileFiltersModal/TypeMenuItemBlock'
import LocationInput from './LocationInput'

export const getTextColor = ({ isError, isFocused }: { isError: boolean; isFocused: boolean }) => {
  if (isFocused) {
    return '#1A3586'
  }

  return isError ? '#CC4F4A' : '#4C5870'
}

const WebView = ({
  onChangeSearchType,
  onChangeSearch,
  onSearchFieldFocus,
  onSearchFieldBlur,
  onChangeLocationSearch,
  isLocationFieldFocused,
  setIsLocationFieldFocused,
  isClientSide,
  getYourLocation,
  handleSearch,
  openSearchTypeDropdown,
  setOpenSearchTypeDropdown,
  ...autocompleteProps
}: HomePageViewProps) => {
  const translate = useTranslateMessage()
  const theme = useTheme()

  const {
    resetKeywordSearch,
    state: { filters, isKeywordChanged, isKeywordValidationFailed, isLocationValidationFailed, isLoadingButtonState }
  } = useContext(HomePageContext)

  const { searchTypes, selectedSearchType } = useHomePageSearchBarConfig()

  const [isTypeFieldHovered, setIsTypeFieldHovered] = useState(false)
  const [isLocationFieldHovered, setIsLocationFieldHovered] = useState(false)
  const [isAutocompleteHovered, setIsAutocompleteHovered] = useState(false)
  const [isFirstOpen, setIsFirstOpen] = useState(true)

  const allFieldsAreFilled = useMemo(
    () => !!filters.selectedKeywordId && !!filters.selectedLocationId,
    [filters.selectedKeywordId, filters.selectedLocationId]
  )

  const handleCloseSearchTypeDown = () => setOpenSearchTypeDropdown(false)

  const changeTypeDropdownState = () => setOpenSearchTypeDropdown((prev: boolean) => !prev)

  useEffect(() => {
    if ((openSearchTypeDropdown || autocompleteProps.isSearchFieldFocused || isLocationFieldFocused) && isFirstOpen) {
      setIsFirstOpen(false)
    }
  }, [isFirstOpen, openSearchTypeDropdown, autocompleteProps.isSearchFieldFocused, isLocationFieldFocused])

  return (
    <Grid component={Hidden} implementation="css" justifyContent="center" sm={8}>
      <Grid container>
        <SearchBarContainer
          container
          isAnyOptionSelected={
            openSearchTypeDropdown || autocompleteProps.isSearchFieldFocused || isLocationFieldFocused
          }
          leftTabHovered={
            isFirstOpen ||
            (isTypeFieldHovered && autocompleteProps.isSearchFieldFocused) ||
            (openSearchTypeDropdown && isAutocompleteHovered)
          }
          rightTabHovered={
            (isLocationFieldHovered && autocompleteProps.isSearchFieldFocused) ||
            (isLocationFieldFocused && isAutocompleteHovered)
          }
        >
          <ClickAwayListener onClickAway={handleCloseSearchTypeDown}>
            <Grid item width="30%">
              <StyledIconButton
                onClick={() => changeTypeDropdownState()}
                disableRipple
                onMouseEnter={() => setIsTypeFieldHovered(true)}
                onMouseLeave={() => setIsTypeFieldHovered(false)}
              >
                {selectedSearchType && (
                  <CustomMenuItem
                    openSearchTypeDropdown={openSearchTypeDropdown || isFirstOpen}
                    isSearchFieldFocused={autocompleteProps.isSearchFieldFocused}
                    isLocationFieldFocused={isLocationFieldFocused}
                  >
                    <ListItemText sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginLeft: '8px' }}>
                      <InputGradientText isActive={openSearchTypeDropdown}>
                        {translate('homepage.searchbar.look')}
                      </InputGradientText>
                      <InputText textAlign="left" isActive={openSearchTypeDropdown}>
                        {selectedSearchType.title}
                      </InputText>
                    </ListItemText>
                    <StyledChevronRightIcon isOpen={openSearchTypeDropdown} />
                  </CustomMenuItem>
                )}
                <StyledMenuList isOpen={openSearchTypeDropdown}>
                  {searchTypes.map(({ value, title, Icon }, index) => (
                    <TypeMenuItemBlock
                      key={value}
                      isFirstItem={index === 0}
                      isLastItem={searchTypes.length - 1 === index}
                      value={value}
                      searchType={filters.searchType}
                      onClick={() => onChangeSearchType(value)}
                      title={title}
                      Icon={Icon}
                    />
                  ))}
                </StyledMenuList>
              </StyledIconButton>
            </Grid>
          </ClickAwayListener>

          <Grid item width="40%">
            <Box position="relative" sx={{ marginLeft: '8px' }}>
              <InputGradientText
                style={{ position: 'absolute', zIndex: 10, left: '14px', top: '14px' }}
                isActive={autocompleteProps.isSearchFieldFocused}
                isError={isKeywordValidationFailed}
              >
                {translate('homepage.searchbar.what')}
              </InputGradientText>
            </Box>
            <AutocompleteSearchTextField
              fullWidth
              onMouseEnter={() => setIsAutocompleteHovered(true)}
              onMouseLeave={() => setIsAutocompleteHovered(false)}
              placeholder={translate('homepage.search.placeholder.second-input')}
              autoComplete="off"
              onChange={(event) => {
                onChangeSearch(event)

                if (!event.target.value?.length) {
                  resetKeywordSearch()
                }
              }}
              value={filters.searchString}
              onFocus={() => {
                if (!isKeywordChanged) {
                  onSearchFieldFocus()
                }
              }}
              onBlur={onSearchFieldBlur}
              isKeywordValidationFailed={isKeywordValidationFailed}
              isBorderLeftHidden={isTypeFieldHovered || openSearchTypeDropdown || isFirstOpen}
              isBorderRightHidden={isLocationFieldHovered || isLocationFieldFocused || isLocationValidationFailed}
              isSearchFieldFocused={autocompleteProps.isSearchFieldFocused}
              openSearchTypeDropdown={openSearchTypeDropdown}
              isLocationFieldFocused={isLocationFieldFocused}
              InputProps={{
                endAdornment: isKeywordChanged ? <LoadingWheel /> : null,
                sx: {
                  borderWidth: 1,
                  borderColor: 'red',
                  marginLeft: '8px',
                  paddingRight: isKeywordChanged ? '9px !important' : '',
                  '& input': {
                    marginTop: '20px',
                    ...(filters.selectedKeywordName === filters.searchString
                      ? {
                          color: getTextColor({ isError: isKeywordValidationFailed, isFocused: true }),
                          fontWeight: 500
                        }
                      : {
                          color: getTextColor({ isError: isKeywordValidationFailed, isFocused: false }),
                          fontWeight: 400
                        })
                  }
                }
              }}
            />
            <ConditionalRenderBox isOpen={autocompleteProps.isSearchFieldFocused} isClientSide={isClientSide}>
              <CustomAutocomplete
                onChangeSearch={onChangeSearch}
                onSearchFieldBlur={onSearchFieldBlur}
                customStyle={
                  !!filters?.searchType && newSearchTypesPractices.includes(filters.searchType)
                    ? theme.custom.SearchBar.customPositionDesktopModal
                    : theme.custom.SearchBar.customPositionDesktopModalForDoctify
                }
                {...autocompleteProps}
              />
            </ConditionalRenderBox>
          </Grid>
          <LocationInput
            getYourLocation={getYourLocation}
            isClientSide={isClientSide}
            isAnyOptionSelected={openSearchTypeDropdown || autocompleteProps.isSearchFieldFocused}
            setIsLocationFieldFocused={setIsLocationFieldFocused}
            setIsLocationFieldHovered={setIsLocationFieldHovered}
            isLocationFieldFocused={isLocationFieldFocused}
            onChangeLocationSearch={onChangeLocationSearch}
          />
        </SearchBarContainer>
        <SearchButtonContainer>
          <SearchIconButton onClick={handleSearch} allFieldsAreFilled={allFieldsAreFilled}>
            {isLoadingButtonState ? (
              <LoadingWheel />
            ) : (
              <SearchBarSearchIcon sx={{ width: '24px', height: '24px', path: { stroke: whiteColor } }} />
            )}
          </SearchIconButton>
        </SearchButtonContainer>
      </Grid>
    </Grid>
  )
}

export default WebView
